
import React, { useEffect } from 'react';
import { ConnectAccountCard } from '@/components/accounts/ConnectAccountCard';
import { PlatformCard } from '@/components/accounts/platform-card/PlatformCard';
import { Separator } from '@/components/ui/separator';
import { useCTraderAccounts } from '@/hooks/use-ctrader-accounts';
import { useSharedImageLoader } from "@/hooks/useSharedImageLoader";
import { useTranslation } from "react-i18next";
import { EnhancedAccountsPageHeader } from '@/components/accounts/EnhancedAccountsPageHeader';
import { useIsMobile } from '@/hooks/use-mobile';

const platforms = [
  {
    name: 'cTrader',
    shortName: 'cTrader',
    logo: 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo_ctrader.png',
    descriptionKey: 'ctrader_description',
    features: [],
    isSupported: true
  },
  {
    name: 'MetaTrader 4',
    shortName: 'MT4',
    logo: 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo_meta4.png',
    descriptionKey: 'mt4_description',
    features: [],
    connected: false,
    isSupported: false
  },
  {
    name: 'MetaTrader 5',
    shortName: 'MT5',
    logo: 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20meta5.png',
    descriptionKey: 'mt5_description',
    features: [],
    connected: false,
    isSupported: false
  }
];

const exchanges = [
  {
    name: 'Binance',
    logo: 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20binance.png',
    descriptionKey: 'binance_description',
    connected: false,
    isSupported: false
  },
  {
    name: 'OKX',
    logo: 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//okx-logo.jpg',
    descriptionKey: 'okx_description',
    connected: false,
    isSupported: false
  },
  {
    name: 'Bybit',
    logo: 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//bybit-logo-white.jpg',
    descriptionKey: 'bybit_description',
    connected: false,
    isSupported: false
  },
  {
    name: 'Bitget',
    logo: 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//images.png',
    descriptionKey: 'bitget_description',
    connected: false,
    isSupported: false
  },
  {
    name: 'BingX',
    logo: 'https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//images.jpg',
    descriptionKey: 'bingx_description',
    connected: false,
    isSupported: false
  }
];

const AccountsPage = () => {
  const { data: ctraderAccounts, isLoading } = useCTraderAccounts();
  const hasCtraderAccount = !isLoading && ctraderAccounts && ctraderAccounts.length > 0;
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Collect all image URLs to preload
  const allImageUrls = [
    ...platforms.map(p => p.logo),
    ...exchanges.map(e => e.logo)
  ];
  
  // Use our enhanced image loader
  const logosLoading = useSharedImageLoader(allImageUrls);

  // Pre-connect to the image hosting domain to speed up subsequent requests
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://lbvhywxvthwgiebjsrlr.supabase.co';
    document.head.appendChild(link);
    
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div className="space-y-8 md:space-y-12 pb-10">
      <EnhancedAccountsPageHeader />
      
      <section data-tour="trading-platforms" className="relative">
        <div className="bg-gradient-to-r from-background to-muted/20 border border-border/50 rounded-xl p-4 md:p-6 shadow-sm">
          <div className="mb-4 md:mb-6">
            <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent`}>
              {t('trading_platforms')}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">{t('connect_trading_platforms')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {platforms.map((platform) => (
              <PlatformCard
                key={platform.name}
                {...platform}
                description={t(platform.descriptionKey)}
                connected={platform.name === 'cTrader' ? hasCtraderAccount : platform.connected}
                className={platform.name === 'cTrader' ? 'platform-card-ctrader border-primary/30 hover:border-primary/50' : ''}
                logosLoading={logosLoading}
              />
            ))}
          </div>
        </div>
      </section>

      <Separator className="my-4 md:my-8" />

      <section data-tour="crypto-exchanges" className="relative">
        <div className="bg-gradient-to-r from-background to-muted/20 border border-border/50 rounded-xl p-4 md:p-6 shadow-sm">
          <div className="mb-4 md:mb-6">
            <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent`}>
              {t('crypto_exchanges')}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">{t('connect_crypto_exchanges')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {exchanges.map((exchange) => (
              <ConnectAccountCard
                key={exchange.name}
                {...exchange}
                description={t(exchange.descriptionKey)}
                className="hover:border-primary/30 transition-all duration-200"
              />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-card to-muted/30 border border-border/50 rounded-xl p-4 md:p-6 mt-8 md:mt-12 shadow-sm">
        <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-medium mb-2 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent`}>
          {t('need_help_connecting')}
        </h2>
        <p className="text-xs md:text-sm text-muted-foreground mb-4">
          {t('connection_help_description')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          <div className="bg-gradient-to-br from-accent to-accent/80 p-3 md:p-4 rounded-xl border border-border/30 hover:border-primary/30 transition-colors">
            <h3 className="font-medium mb-1 md:mb-2 text-sm md:text-base">{t('api_key_setup_guide')}</h3>
            <p className="text-xs text-muted-foreground">{t('api_key_setup_description')}</p>
          </div>
          <div className="bg-gradient-to-br from-accent to-accent/80 p-3 md:p-4 rounded-xl border border-border/30 hover:border-primary/30 transition-colors">
            <h3 className="font-medium mb-1 md:mb-2 text-sm md:text-base">{t('security_best_practices')}</h3>
            <p className="text-xs text-muted-foreground">{t('security_practices_description')}</p>
          </div>
          <div className="bg-gradient-to-br from-accent to-accent/80 p-3 md:p-4 rounded-xl border border-border/30 hover:border-primary/30 transition-colors">
            <h3 className="font-medium mb-1 md:mb-2 text-sm md:text-base">{t('exchange_compatibility')}</h3>
            <p className="text-xs text-muted-foreground">{t('compatibility_description')}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountsPage;
